<script setup lang="ts">
import { useAuthStore } from '~/stores/auth.store';
import type { IUploadedDocs } from '~/interfaces/dto/compliance/documentVerificationInterfaces';
import AccordionDocVerification from '../design-system/accordion-doc-verification.vue';
import Button from '~/components/design-system/button.vue';

const { $t, $enabled, $trackEvent } = useNuxtApp();
const { $complianceService, $authService } = useServices();
const auth = useAuthStore();
const siteStore = useSiteStore();
const rg = useRgStore();
const idDocs: IUploadedDocs[] = reactive([]);
const bankDocs: IUploadedDocs[] = reactive([]);
const supportingDocs: IUploadedDocs[] = reactive([]);
const idStatus = ref<number>();
const bankStatus = ref<number>();
const supportingStatus = ref<number>();
const ficaApproved = ref<boolean>(false);
const loader = ref<boolean>(true);

const emits = defineEmits<{ (e: 'activateSumSub'): void }>();

const requiresSumSub = computed(() => auth.getSumsubRequired);

function uploadFile(document: IUploadedDocs, thumbnail?) {
  $trackEvent('Account', 'DOC_VERIFICATION_SUBMIT');
  //display newly uploading pending files
  const doc = document;
  const docType = doc.documentType;

  doc.thumbnail = thumbnail;
  if (docType === 0 || docType === 3) {
    idDocs.push(doc);
  } else {
    if (docType === 2) {
      bankDocs.push(doc);
    } else {
      supportingDocs.push(doc);
    }
  }
}

function deleteFile(file: IUploadedDocs) {
  const docType = file.documentType;
  if (docType === 0 || docType === 3) {
    //proofOfID
    for (let i = 0; i < idDocs.length; i++) {
      if (idDocs[i].id === file.id) {
        idDocs.splice(i, 1);
      }
    }
  } else {
    if (docType === 2) {
      //bank docs
      for (let i = 0; i < bankDocs.length; i++) {
        if (bankDocs[i].id === file.id) {
          bankDocs.splice(i, 1);
        }
      }
    } else {
      //supporting docs
      for (let i = 0; i < supportingDocs.length; i++) {
        if (supportingDocs[i].id === file.id) {
          supportingDocs.splice(i, 1);
        }
      }
    }
  }
}

async function getDocs() {
  await $complianceService
    .getDocuments()
    .then((data) => {
      //if data not successful display error TODO
      if (data?.isSuccessful) {
        const docs = data?.data?.documents;
        //refer to notes for the rest of this algo
        docs.forEach((doc) => {
          if (doc.documentType === 0 || doc.documentType === 3) {
            //proofOfID
            idDocs.push(doc);
          } else {
            if (doc.documentType === 2) {
              bankDocs.push(doc);
            } else {
              //supporting docs
              supportingDocs.push(doc);
            }
          }
        });
      }
    })
    .catch((error) => {});
}

const documentVerificationKey = computed(() => {
  if (requiresSumSub.value) return 'sum-sub-doc-ver-id-verification';
  return 'doc-ver-id-verification';
});

function initDocVer() {
  idStatus.value = auth.getIdDocumentStatus;
  bankStatus.value = auth.getBankDocumentStatus;
  supportingStatus.value = auth.getSupportingDocumentStatus;
  ficaApproved.value = auth.getComplianceStatus === 1;
}

onBeforeMount(async () => {
  await $authService.getUserExtended().then(() => {
    loader.value = false;
  }); //calling this so that compliance values are updated if navigated immediately after post reg
  if (rg.hasPartialRegLock && $enabled('myaccount.enablepartialreg')) {
    auth.toggleAccountModal(false);
    siteStore.activateModal('complete-registration');
  }
  $trackEvent('Account', 'MY_ACCOUNT_SETTINGS', {
    to: 'document verification',
  });
  initDocVer();
  await getDocs();
});
</script>

<template>
  <div class="relative">
    <LazyUserInterfaceGenericLoader v-if="loader" container />
    <div
      class="mx-auto p-2 md:py-4 dark:text-light-50 lg:w-190"
      :class="loader ? 'blur-4' : 'blur-0'"
    >
      <div class="bg-primary md:bg-layer rounded-lg text-sm">
        <p class="ml-2 pt-3 mb-0 text-base-priority text-base font-bold">
          {{
            $t(
              `document-verification-${siteStore.getRegionCode}`,
              `document-verification`,
            )
          }}
        </p>
        <div
          class="p-2 mb-2 md:mb-4"
          v-html="
            $t(
              `doc-ver-explainer-${siteStore.getRegionCode}`,
              'doc-ver-explainer',
            )
          "
        />
      </div>
      <AccordionDocVerification
        header="identity-verification"
        :header-status="idStatus"
        :documents="idDocs"
        :display-document-upload="!requiresSumSub"
        document-type="ProofOfIdDocument"
        @delete-file="deleteFile"
        @upload-file="uploadFile"
      >
        <template #accordionContent>
          <div class="space-y-2">
            <div
              v-html="
                $t(
                  `${documentVerificationKey}-${siteStore.getRegionCode}`,
                  documentVerificationKey,
                )
              "
            ></div>
            <Button
              v-if="requiresSumSub"
              class="w-[150px] justify-center"
              :disabled="ficaApproved"
              @click="emits('activateSumSub')"
            >
              {{ $t('verify-now') }}
            </Button>
          </div>
        </template>
      </AccordionDocVerification>
      <AccordionDocVerification
        v-if="ficaApproved"
        header="bank-verification"
        :header-status="bankStatus"
        :documents="bankDocs"
        :display-tag="!requiresSumSub"
        value="1"
        document-type="ProofOfBankDocument"
        @delete-file="deleteFile"
        @upload-file="uploadFile"
      >
        <template #accordionContent>
          <div
            v-html="
              $t(
                `doc-ver-bank-verification-${siteStore.getRegionCode}`,
                'doc-ver-bank-verification',
              )
            "
          ></div>
        </template>
      </AccordionDocVerification>
      <AccordionDocVerification
        v-if="ficaApproved"
        header="supporting-documentation"
        :header-status="supportingStatus"
        :display-tag="!requiresSumSub"
        :documents="supportingDocs"
        value="2"
        document-type="ProofOfResidenceDocument"
        @delete-file="deleteFile"
        @upload-file="uploadFile"
      >
        <template #accordionContent>
          <div class="my-2">
            <p class="pb-2 my-0">
              {{
                $t(
                  `doc-ver-supporting-docs-${siteStore.getRegionCode}`,
                  'doc-ver-supporting-docs',
                )
              }}
            </p>
          </div>
        </template>
      </AccordionDocVerification>
    </div>
  </div>
</template>
